import {
  AfterViewInit,
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoaderHandler } from 'planning-portal-library/src/lib/core/domain';
import { nameof } from 'ts-simple-nameof/src/nameof';
import { FileUploadModule } from 'primeng/fileupload';
import { PlanningPortalBaseComponent } from '../../base/planning-portal-base/planning-portal-base.component';
import { GetSampleJLUploadFilesUseCase } from 'planning-portal-library/src/lib/core/usecases/configuration';
import { ImportJobsCSVUseCase } from 'planning-portal-library/src/lib/core/usecases/plans';
import { ProgressBar } from 'primeng/progressbar';
import { ValidateJLCsvFileUseCase } from 'planning-portal-library/src/lib/core/usecases/plans';
import { TranslateDirective, TranslatePipe } from '@embraceit/angularcore/tms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom, Observable } from 'rxjs';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import saveAs from 'file-saver';
import { FormsModule } from '@angular/forms';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MasterPlansState } from '../state/master-plan.state';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'planning-portal-import-jobs-dialog',
  imports: [
    CommonModule,
    FileUploadModule,
    Select,
    TranslatePipe,
    FormsModule,
    ProgressBar,
    ButtonModule,
  ],
  templateUrl: './import-jobs-dialog.component.html',
  styles: [],
})
export class ImportJobsDialogComponent
  extends PlanningPortalBaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  uploadForm!: FormGroup;
  cancelFlag = false;
  planId!: string;
  planContainerId!: string;
  openFromPlan: false;
  defaultDelimiter!: string;
  private getSampleJLUploadFilesUsecase: GetSampleJLUploadFilesUseCase;
  private validateJLCsvFile: ValidateJLCsvFileUseCase;

  constructor(
    inject: Injector,
    private fb: FormBuilder,
    public config: DynamicDialogConfig,
    private dialogRef: DynamicDialogRef
  ) {
    super(inject);
    this.planId = this.config.data.planId;
    this.planContainerId = this.config.data.planContainerId;
    this.openFromPlan = this.config.data.openFromPlan;
    this.getSampleJLUploadFilesUsecase = inject.get(
      GetSampleJLUploadFilesUseCase
    );
    this.validateJLCsvFile = inject.get(ValidateJLCsvFileUseCase);
  }

  ngOnInit(): void {
    this.initForm();
  }
  ngAfterViewInit() {
    this.browseButtonListener();
  }
  initForm() {
    this.defaultDelimiter = ',';
  }
  delimiters: any[] = [
    { name: ';', value: ';' },
    { name: ',', value: ',' },
  ];
  isFileUploaded = false;
  isFileUploadStarted = false;
  file: File | any = null;
  maxFileSize = 10000000000000000;
  progressBarValue = 0;

  onSelect(event: any) {
    if (event.files && event.files.length > 0) {
      this.file = event.files?.item(0);
      this.isFileUploaded = true;
    }
    setTimeout(() => {
      this.getErrorMessage();
    });
  }
  async onUpload() {
    this.isFileUploadStarted = true;
    const loaderHandler: LoaderHandler = {
      componentName: ImportJobsDialogComponent.name,
      endPointName: nameof<ImportJobsDialogComponent>((pc) => pc.onUpload),
    };

    this.enableLoading(loaderHandler);
    try {
      const planCsvFile = this.file;
      const formData = new FormData();
      formData.append('jobZipFile', planCsvFile);
      formData.append('planID', this.planId);
      //formData.append('distributionLaneId', data.distributionLanes ?? '');
      formData.append('delimiter', this.defaultDelimiter ?? '');
      this.cancelOperation(false);
      this.generateRandomNumbers();
      const importPlan$ = this.validateJLCsvFile.execute(formData);
      const importPlan = await lastValueFrom(importPlan$, {
        defaultValue: undefined,
      });
      if (importPlan) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.messages.success.success'),
          detail: this.translate.instant(
            'master_plans.jobs.messages.jl_validated_successfully'
          ),
          life: 3000,
        });
        if (this.openFromPlan) {
          this.moveToJobImportFromPlan(importPlan, this.planContainerId);
        } else {
          this.moveToJobImport(importPlan);
        }

        this.dialogRef.close(true);
      }
    } catch (e: any) {
      this.cancelOperation(true);
      this.progressBarValue = 0;
      let message = 'common.messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }
  cancelOperation(flag: boolean) {
    // Set the cancellation flag to true
    this.cancelFlag = flag;
  }
  private getFileName(header: string): string {
    const contentDispositionParsed: string[] = header.split(';');
    const fileNameLiteral = contentDispositionParsed.find((lit) =>
      lit.includes('filename=')
    );
    return fileNameLiteral?.split('=')[1] ?? '';
  }
  async generateRandomNumbers(): Promise<void> {
    for (let i = 0; i <= 100; i++) {
      if (this.cancelFlag) {
        return; // Exit the function early
      }
      const randomNumber = Math.floor(Math.random() * 101);
      if (randomNumber > this.progressBarValue) {
        this.progressBarValue = randomNumber;
      }
      await this.delay(2000);
    }
  }
  delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  private downloadFile(response: HttpResponse<Blob>, fileName: string) {
    const blob = new Blob([response.body ?? ''], {
      type:  response.body!.type || 'application/zip',
    });
    saveAs(blob, fileName, { autoBom: true });
  }
  async downloadSampleFiles() {
    const loaderHandler: LoaderHandler = {
      componentName: ImportJobsDialogComponent.name,
      endPointName: nameof<ImportJobsDialogComponent>(
        (pc) => pc.downloadSampleFiles
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const sampleNetworkFiles$ = this.getSampleJLUploadFilesUsecase.execute();
      const sampleNetworkFilesResponse = await lastValueFrom(
        sampleNetworkFiles$,
        {
          defaultValue: undefined,
        }
      );
      if (sampleNetworkFilesResponse) {
        const fileName = this.getFileName(
          sampleNetworkFilesResponse.headers.get('content-disposition') ?? ''
        );
        this.downloadFile(sampleNetworkFilesResponse, fileName);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.messages.success.success'),
          detail: this.translate.instant(
            'master_plans.jobs.messages.sample_file_downloaded_successfully'
          ),
          life: 3000,
        });
      }
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }
  cancleFile() {
    this.file = undefined;
    this.isFileUploaded = false;
  }
  chooseFile(event: any, callback: any) {
    callback();
  }
  dismiss(flag: unknown) {
    this.dialogRef?.close(flag);
  }
  ngOnDestroy(): void {}
}
